import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { SiteNav } from './SiteNav';
import { Footer } from './Footer';
import { QSCallCTA } from './QSCallCTA';
import { QSFloatingCTA } from './QSFloatingCTA';
import OptionalAddonsPanel from './OptionalAddonsPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPEC_LABEL = { low: 'Value', mid: 'Standard', high: 'Premium' };

// Status chip vocabulary (Feb 2026 brief)
const STATUS_CHIP = {
  draft:            { label: 'Draft',             bg: '#F1F5F9', fg: '#475569', border: '#E2E8F0' },
  measuring:        { label: 'Measuring',         bg: '#EFF6FF', fg: '#1D4ED8', border: '#BFDBFE' },
  ready_to_unlock:  { label: 'Ready to unlock',   bg: '#ECFDF5', fg: '#15803D', border: '#A7F3D0' },
  paid:             { label: '✓ Reports unlocked', bg: '#DCFCE7', fg: '#166534', border: '#86EFAC' },
};

// Palette — matches LandingPageV2 + p7.html canvas
const C = {
  bg: '#F5F7FA',
  bg2: '#EEF1F6',
  card: '#FFFFFF',
  border: '#D9E2EE',
  border2: '#CBD5E1',
  navy: '#0F2742',
  navy2: '#1a3a5c',
  navy3: '#0a1d31',
  text: '#1E293B',
  text2: '#4a5e72',
  muted: '#64748B',
  muted2: '#94A3B8',
  blue: '#2563EB',
  blueDark: '#1D4ED8',
  green: '#16A34A',
  greenDark: '#15803D',
  amber: '#D97706',
  amberBg: '#EFF6FF',
  amberBorder: '#93C5FD',
};

const DEFECT_LABELS = {
  hasOldBoiler: 'Old boiler',
  hasDraughtyWindows: 'Draughty windows',
  hasRoofProblems: 'Roof problems',
  hasDamp: 'Damp',
  hasArtex: 'Asbestos / Artex',
  hasDamaged: 'Damaged plaster',
  hasOldKitchen: 'Old kitchen',
  hasStructuralCracks: 'Structural cracks',
  hasRewireNeeded: 'Rewire',
};

const formatGBP = (n) => (typeof n === 'number' ? '£' + Math.round(n).toLocaleString('en-GB') : '—');

const fmtDate = (s) => {
  if (!s) return '';
  try { return new Date(s).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return ''; }
};

function ProjectCard({ d, onUnlock, onContinue, onView, onDelete, isOpen }) {
  const total = d.last_project_total;
  const flagged = d.flagged_defects || [];
  const status = d.status || (d.documents_paid ? 'paid' : 'draft');
  const chip = STATUS_CHIP[status] || STATUS_CHIP.draft;
  return (
    <div data-testid={`project-card-${d.draft_id}`}
      style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 22, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            {d.method === 'scopevision' ? 'SCOPEvision' : d.method === 'draw' ? 'Drawn plan' : 'Dimensions'}
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: C.navy, marginTop: 4, lineHeight: 1.3 }}>
            {d.project_name || d.address || 'Untitled project'}
          </div>
          {d.address && d.project_name && (
            <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{d.address}</div>
          )}
          <div style={{ fontSize: 11, color: C.muted2, marginTop: 4 }}>Updated {fmtDate(d.updated_at)}</div>
        </div>
        <span data-testid={`status-chip-${status}`}
          style={{ padding: '4px 10px', borderRadius: 999, background: chip.bg, color: chip.fg, fontSize: 11, fontWeight: 700, border: `1px solid ${chip.border}`, whiteSpace: 'nowrap' }}>
          {chip.label}
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, padding: '10px 0', borderTop: `1px solid ${C.bg2}`, borderBottom: `1px solid ${C.bg2}` }}>
        <div>
          <div style={{ fontSize: 10, color: C.muted2, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Indicative total</div>
          <div data-testid="card-total" style={{ fontSize: 22, fontWeight: 700, color: C.navy, letterSpacing: '-0.02em' }}>{formatGBP(total)}</div>
        </div>
        <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
          <div style={{ fontSize: 10, color: C.muted2, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Spec / rooms</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.text2 }}>
            {SPEC_LABEL[d.spec_level] || 'Standard'} · {d.rooms_count || 0} rooms
          </div>
        </div>
      </div>

      {flagged.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          {flagged.slice(0, 5).map(f => (
            <span key={f} style={{ fontSize: 10.5, padding: '3px 8px', borderRadius: 999, background: C.amberBg, color: '#1E40AF', border: `1px solid ${C.amberBorder}` }}>
              {DEFECT_LABELS[f] || f}
            </span>
          ))}
          {flagged.length > 5 && (
            <span style={{ fontSize: 10.5, padding: '3px 8px', borderRadius: 999, color: C.muted2 }}>+{flagged.length - 5}</span>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
        <button data-testid={`btn-continue-${d.draft_id}`}
          onClick={() => onContinue(d)}
          style={{ flex: 1, minWidth: 130, background: d.documents_paid ? C.navy : '#fff', color: d.documents_paid ? '#fff' : C.navy, border: d.documents_paid ? 'none' : `1.5px solid ${C.border}`, padding: '10px 16px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
          Refine in SCOPE →
        </button>
        {!d.documents_paid && (
          <button data-testid={`btn-unlock-${d.draft_id}`}
            onClick={() => onUnlock(d)}
            style={{ flex: 1, minWidth: 130, background: C.navy, color: '#fff', border: 'none', padding: '10px 16px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            Unlock reports →
          </button>
        )}
        {d.documents_paid && (
          <button data-testid={`btn-downloads-${d.draft_id}`}
            onClick={() => onView && onView(d)}
            style={{ background: '#fff', color: C.navy, border: `1.5px solid ${C.border}`, padding: '10px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            {isOpen ? 'Hide downloads' : 'View downloads'}
          </button>
        )}
        {/* Delete — labeled button so it's discoverable. iter61 moved out of
            top-right corner where it was being hidden by the "Reports unlocked"
            green chip on paid projects. */}
        <button
          data-testid={`btn-delete-${d.draft_id}`}
          onClick={(e) => { e.stopPropagation(); onDelete && onDelete(d); }}
          aria-label="Delete project"
          title="Delete this project"
          style={{
            background: '#fff', color: '#B91C1C', border: `1.5px solid #FECACA`,
            padding: '10px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
            cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#FEF2F2'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
        >
          <span style={{ fontSize: 14 }}>🗑</span> Delete
        </button>
      </div>

      {/* Inline downloads — paid drafts always reveal their tiles directly in
          the card so users can grab them without hunting through navigation. */}
      {d.documents_paid && isOpen && (
        <div data-testid={`downloads-inline-${d.draft_id}`}
          style={{ borderTop: `1px solid ${C.bg2}`, paddingTop: 14, marginTop: 4 }}>
          <DocumentTiles draft={d} />
        </div>
      )}
    </div>
  );
}

function DocumentTiles({ draft }) {
  const navigate = useNavigate();
  // Customer dashboard — only ever shows the documents the user paid for.
  // The QS Internal Report (qs-internal.xlsx) is admin-only and is NEVER
  // surfaced here, even for fully-paid customers (per Apr-2026 brief).
  // The set of documents shown is gated by `documents_paid_tier`:
  //   • scope_materials → Materials only
  //   • scope_sow       → Materials + SoW (PDF + Excel)
  //   • scope_full      → All 4 customer-facing docs
  const tier = draft?.documents_paid_tier || 'scope_full';
  const allDocs = [
    { key: 'client-estimate.pdf', label: 'Cost Estimate', icon: '📄', color: C.navy, tiers: ['scope_full'] },
    { key: 'sow.xlsx',            label: 'Schedule of Works (Excel)', icon: '📊', color: C.green, tiers: ['scope_full', 'scope_sow'] },
    { key: 'sow.pdf',             label: 'Schedule of Works (PDF)',   icon: '📋', color: C.navy,  tiers: ['scope_full', 'scope_sow'] },
    { key: 'materials.pdf',       label: 'Materials Schedule',         icon: '📦', color: C.navy,  tiers: ['scope_full', 'scope_sow', 'scope_materials'] },
  ];
  const docs = allDocs.filter(d => d.tiers.includes(tier));
  // Build an upgrade-offer card for the docs the user *didn't* buy. Lets them
  // top up from the dashboard without going back through the SCOPE flow.
  const lockedDocs = allDocs.filter(d => !d.tiers.includes(tier));
  const upgradeTiers = [];
  if (tier === 'scope_materials') {
    upgradeTiers.push(
      { key: 'scope_sow',  price: '£90',  label: 'Add Schedule of Works',  desc: 'Tender-ready SoW (PDF + Excel) — credit applied' },
      { key: 'scope_full', price: '£170', label: 'Upgrade to Full Project Pack', desc: 'Adds Cost Estimate + tender guide — credit applied' },
    );
  } else if (tier === 'scope_sow') {
    upgradeTiers.push(
      { key: 'scope_full', price: '£80',  label: 'Upgrade to Full Project Pack', desc: 'Adds Cost Estimate + tender guide — credit applied' },
    );
  }
  return (
    <div data-testid="document-tiles-wrap" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
        {docs.map(d => {
          const url = `${process.env.REACT_APP_BACKEND_URL}/api/scope/drafts/${draft.draft_id}/${d.key}`;
          // Feb 2026 — switched from <a href download> to a JS blob download
          // because Safari's ITP routinely strips httpOnly cookies on cross-
          // origin <a> requests, which produced 401s on production. axios
          // already mirrors the JWT into an `Authorization: Bearer` header,
          // so this path works regardless of cookie state.
          const onClick = async (e) => {
            e.preventDefault();
            try {
              const res = await axios.get(url, { responseType: 'blob' });
              // Sniff the filename out of the response Content-Disposition;
              // fall back to the route-level key for parity.
              let filename = d.key;
              const cd = res.headers && (res.headers['content-disposition'] || res.headers['Content-Disposition']);
              if (cd) {
                const m = /filename\s*=\s*"?([^";]+)"?/i.exec(cd);
                if (m) filename = m[1];
              }
              const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/octet-stream' });
              const blobUrl = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = blobUrl; a.download = filename;
              document.body.appendChild(a);
              a.click();
              setTimeout(() => { try { URL.revokeObjectURL(blobUrl); a.remove(); } catch {} }, 200);
            } catch (err) {
              const status = err?.response?.status;
              if (status === 401) {
                // Bounce through /login — auth context lost its grip
                navigate(`/login?next=${encodeURIComponent(`/dashboard?next_draft=${draft.draft_id}`)}`);
                return;
              }
              if (status === 402 || status === 403) {
                alert(err?.response?.data?.error || 'This document is not included in your purchase. Use the upgrade option below.');
                return;
              }
              if (status === 422) {
                // MEASUREMENT_INCOMPLETE — blob response wraps the JSON error.
                // Read it out so we can show the friendly message.
                let msg = 'We couldn\'t read your measurements — please re-open this project in SCOPE and re-confirm your rooms.';
                try {
                  const blobText = await err.response.data.text();
                  const json = JSON.parse(blobText);
                  if (json && json.message) msg = json.message;
                } catch {}
                alert(msg);
                return;
              }
              alert('Could not download — please refresh the page and try again.');
            }
          };
          return (
            <a key={d.key} data-testid={`doc-tile-${d.key}`}
              href={url}
              onClick={onClick}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
                background: '#fff', border: `1px solid rgba(37,99,235,.3)`,
                borderRadius: 10, textDecoration: 'none', cursor: 'pointer', transition: '0.15s',
              }}>
              <div style={{ fontSize: 22 }}>{d.icon}</div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: d.color }}>{d.label}</div>
                <div style={{ fontSize: 11, color: C.muted }}>Click to download</div>
              </div>
            </a>
          );
        })}
      </div>
      {lockedDocs.length > 0 && (
        <div data-testid="locked-docs-upgrade" style={{
          padding: '14px 16px',
          background: 'linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)',
          border: '1px dashed #F59E0B', borderRadius: 10,
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#92400E', marginBottom: 6 }}>
            🔒 Want the rest? You currently have {tier === 'scope_materials' ? 'the Materials Schedule' : 'the Schedule of Works'}.
          </div>
          <div style={{ fontSize: 11.5, color: '#78350F', lineHeight: 1.5, marginBottom: 10 }}>
            Locked: {lockedDocs.map(d => d.label).join(', ')}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {upgradeTiers.map(u => (
              <button key={u.key}
                data-testid={`dash-upgrade-${u.key}`}
                onClick={async () => {
                  try {
                    const res = await axios.post(`${API}/scope/drafts/${draft.draft_id}/documents-checkout`, {
                      origin: window.location.origin, tier: u.key,
                    }, { withCredentials: true });
                    if (res.data?.url) {
                      window.location.href = res.data.url;
                    } else if (res.data?.already_paid) {
                      alert('You already own this tier. Pick a higher one to upgrade.');
                    } else {
                      alert('Could not start upgrade. Please try again.');
                    }
                  } catch (e) {
                    alert(e?.response?.data?.error || 'Could not start upgrade. Please try again.');
                  }
                }}
                style={{
                  background: '#0F2742', color: '#fff', border: 'none',
                  padding: '9px 14px', borderRadius: 8,
                  fontSize: 12, fontWeight: 700, cursor: 'pointer',
                  fontFamily: 'inherit',
                }}>
                {u.label} — {u.price}
                <div style={{ fontSize: 10, fontWeight: 500, marginTop: 2, opacity: 0.85 }}>{u.desc}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function DashboardPage() {
  const { user, loading: authLoading, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [resending, setResending] = useState(false);
  const [openDraftId, setOpenDraftId] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (user === false) { navigate('/login?next=/dashboard'); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/scope/dashboard`);
        if (cancelled) return;
        setData(res.data);
        // Auto-open the most recent paid draft so the user can see + grab
        // their downloads the moment they hit the dashboard. Falls back to
        // the most recent draft of any kind so the projects list is still
        // implicitly highlighted.
        const drafts = res.data?.drafts || [];
        // Feb 2026 — when arriving from p7.html's "Go to my dashboard" auth
        // gate, the URL carries ?next_draft={id}. Open that specific card
        // so the user sees both the unlock CTA and (if paid) the downloads
        // inline. Falls back to the most-recent paid project otherwise.
        const wantedDraft = search.get('next_draft');
        const wantedHit = wantedDraft && drafts.find(d => d.draft_id === wantedDraft);
        if (wantedHit) {
          setOpenDraftId(wantedHit.draft_id);
          setTimeout(() => {
            const el = document.querySelector(`[data-testid="project-card-${wantedHit.draft_id}"]`);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 120);
          // Strip the query param so a refresh doesn't re-trigger.
          try {
            const clean = new URL(window.location.href);
            clean.searchParams.delete('next_draft');
            window.history.replaceState({}, '', clean.pathname + (clean.search || '') + clean.hash);
          } catch {}
        } else {
          const firstPaid = drafts.find(d => d.documents_paid);
          if (firstPaid) setOpenDraftId(firstPaid.draft_id);
        }
      } catch (e) {
        if (!cancelled) setError('Could not load your dashboard.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, authLoading, navigate, search]);

  // Background polling for the post-checkout return — does NOT gate initial render.
  // useRef guards against the infinite loop where setData() → `data` changes →
  // effect re-fires → toast re-fires → another setData(), etc.
  const postCheckoutFiredRef = useRef(null);
  useEffect(() => {
    if (!data) return;
    const sessionId = search.get('session_id');
    const draft = search.get('draft');
    const paid = search.get('paid');
    if (!(sessionId && draft && paid === '1')) return;
    // Prevent re-firing for the same Stripe session across re-renders.
    if (postCheckoutFiredRef.current === sessionId) return;
    let cancelled = false;
    (async () => {
      for (let i = 0; i < 8; i++) {
        if (cancelled) return;
        try {
          const ps = await axios.get(`${API}/scope/drafts/${draft}/documents-status`, {
            params: { session_id: sessionId },
          });
          if (ps.data.payment_status === 'paid' || ps.data.already_processed) {
            // Mark BEFORE setData so the effect doesn't re-enter on the next render.
            postCheckoutFiredRef.current = sessionId;
            toast.success('Reports unlocked! Check your email.');
            try {
              const r2 = await axios.get(`${API}/scope/dashboard`);
              if (!cancelled) { setData(r2.data); setOpenDraftId(draft); }
            } catch {}
            // Strip the session_id / paid query params so a hard-refresh doesn't
            // re-fire the celebration path (and any absent-minded bookmark is safe).
            try {
              const clean = new URL(window.location.href);
              clean.searchParams.delete('session_id');
              clean.searchParams.delete('paid');
              window.history.replaceState({}, '', clean.pathname + (clean.search ? clean.search : '') + clean.hash);
            } catch {}
            return;
          }
        } catch (e) { /* keep polling */ }
        await new Promise(r => setTimeout(r, 2000));
      }
    })();
    return () => { cancelled = true; };
  }, [data, search]);

  const handleContinue = (d) => {
    // Skip the intake modal on /p7.html when we're coming back from the
    // dashboard — the user has already given us address, postcode, name,
    // and previous canvas state. `from_dashboard=1` flips the intake
    // overlay off and routes the bridge straight to chat resumption.
    window.location.href = `/p7.html?draft=${d.draft_id}&from_dashboard=1`;
  };

  const handleViewDownloads = (d) => {
    setOpenDraftId(prev => prev === d.draft_id ? null : d.draft_id);
    // Smooth scroll to the card so the inline tiles are visible.
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="project-card-${d.draft_id}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 80);
  };

  const handleUnlock = async (d) => {
    // Send the user to the SCOPE tool's purchase phase where they can pick
    // the tier (Materials £29 / SoW £119 / Full Pack £199). Calling
    // documents-checkout directly here would silently default to the £199
    // Full Pack — bad UX. The purchase phase shows tier choice + a sample
    // SoW preview before checkout.
    window.location.href = `/scope/draw?draft=${d.draft_id}&phase=purchase`;
  };

  const handleDelete = async (d) => {
    // Soft-delete (archive) — server sets archived_at so the dashboard
    // hides it but admin can still see/restore in the Projects tab.
    // Paid drafts get a stronger warning since the user loses re-download
    // access until an admin restores.
    const isPaid = !!d.documents_paid;
    const label = d.project_name || d.address || 'this project';
    const msg = isPaid
      ? `Delete "${label}"?\n\nThis project has paid documents. Deleting will REMOVE your ability to re-download the Cost Estimate / SoW / Materials Schedule from your dashboard. Contact support if you need to restore.`
      : `Delete "${label}"?\n\nIt will be removed from your dashboard. Contact support within 30 days if you need to restore.`;
    if (!window.confirm(msg)) return;
    try {
      await axios.post(
        `${API}/scope/drafts/${d.draft_id}/archive`,
        {},
        { withCredentials: true },
      );
      if (openDraftId === d.draft_id) setOpenDraftId(null);
      // Optimistic remove — refetch in parallel for safety.
      setData((prev) => prev && ({
        ...prev,
        drafts: (prev.drafts || []).filter((x) => x.draft_id !== d.draft_id),
      }));
      toast.success('Project deleted');
    } catch (e) {
      const msg2 = e?.response?.data?.error || 'Could not delete project — please try again.';
      toast.error(msg2);
    }
  };

  const handleResendVerify = async () => {
    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-verify-email`);
      toast.success('Verification email sent. Check your inbox.');
    } catch (e) {
      toast.error('Could not re-send email. Please try again later.');
    } finally {
      setResending(false);
    }
  };

  const newProject = () => { window.location.href = '/p7.html'; };

  if (authLoading || loading) {
    return <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted, background: C.bg }}>Loading dashboard…</div>;
  }
  if (error) return <div style={{ padding: 40, color: '#DC2626', background: C.bg }}>{error}</div>;
  const drafts = data?.drafts || [];
  const me = data?.user || {};
  const openDraft = drafts.find(d => d.draft_id === openDraftId);

  return (
    <div data-testid="dashboard-page" style={{ minHeight: '100vh', background: C.bg, color: C.text, fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif', display: 'flex', flexDirection: 'column' }}>
      <SiteNav cta="home" />
      {/* Sidebar layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', flex: 1 }}>
        <aside style={{ background: C.navy, color: '#fff', padding: '28px 20px', position: 'sticky', top: 0, alignSelf: 'start', minHeight: '100vh', borderRight: `1px solid ${C.navy3}` }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.16em', color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', marginBottom: 4 }}>YOUR ACCOUNT</div>
          <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.01em', color: '#fff' }}>Dashboard</div>
          <nav style={{ marginTop: 32, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <a href="#projects" data-testid="nav-projects" style={{ padding: '10px 12px', borderRadius: 8, background: 'rgba(255,255,255,0.08)', color: '#fff', textDecoration: 'none', fontSize: 13, fontWeight: 600 }}>📁 My Projects</a>
            <a href="/p7.html" data-testid="nav-new" style={{ padding: '10px 12px', borderRadius: 8, color: 'rgba(255,255,255,0.7)', textDecoration: 'none', fontSize: 13, fontWeight: 600 }}>＋ New project (SCOPE)</a>
            <a href="/estimate" data-testid="nav-ballpark" style={{ padding: '10px 12px', borderRadius: 8, color: 'rgba(255,255,255,0.7)', textDecoration: 'none', fontSize: 13, fontWeight: 600 }}>💷 Get a ballpark estimate</a>
            <button onClick={(e) => {
              e.preventDefault();
              // Documents nav — opens the most recent paid draft's downloads
              // and scrolls to it. If the user has no paid drafts yet, point
              // them at the first project they've started so the unlock card
              // is highlighted instead.
              const firstPaid = drafts.find(d => d.documents_paid);
              const target = firstPaid || drafts[0];
              if (!target) return;
              if (firstPaid) setOpenDraftId(firstPaid.draft_id);
              setTimeout(() => {
                const el = document.querySelector(`[data-testid="project-card-${target.draft_id}"]`);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 80);
            }} data-testid="nav-documents" style={{ background: 'transparent', border: 'none', textAlign: 'left', padding: '10px 12px', borderRadius: 8, color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' }}>📄 Documents</button>
            <a href="#qs-call" data-testid="nav-qs-call" style={{ padding: '10px 12px', borderRadius: 8, color: 'rgba(255,255,255,0.7)', textDecoration: 'none', fontSize: 13, fontWeight: 600 }}>📞 QS Call</a>
            <Link to="/account" data-testid="nav-account" style={{ padding: '10px 12px', borderRadius: 8, color: 'rgba(255,255,255,0.7)', textDecoration: 'none', fontSize: 13, fontWeight: 600 }}>👤 Account</Link>
          </nav>
          <div style={{ marginTop: 28, padding: '12px 14px', background: 'rgba(255,255,255,0.04)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.08)' }}>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 6 }}>Signed in as</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#fff', wordBreak: 'break-all' }}>{me.email}</div>
            <button data-testid="logout-btn" onClick={() => logout().then(() => navigate('/'))} style={{ marginTop: 12, fontSize: 12, color: 'rgba(255,255,255,0.7)', background: 'transparent', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', width: '100%' }}>Sign out</button>
          </div>
        </aside>

        <main style={{ padding: '36px 44px', maxWidth: 1200 }}>
          {/* Verify-email banner */}
          {me.email && !me.email_verified && (
            <div data-testid="verify-banner" style={{ marginBottom: 28, padding: '14px 18px', background: C.amberBg, border: `1px solid ${C.amberBorder}`, borderRadius: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1E40AF' }}>✉️ Please verify your email</div>
                <div style={{ fontSize: 12, color: '#1E3A8A', marginTop: 2 }}>We sent a link to <strong>{me.email}</strong>. You'll need to verify before unlocking detailed reports.</div>
              </div>
              <button data-testid="resend-verify-btn" onClick={handleResendVerify} disabled={resending}
                style={{ background: '#2563EB', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: resending ? 'wait' : 'pointer' }}>
                {resending ? 'Sending…' : 'Re-send email'}
              </button>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 24, flexWrap: 'wrap', gap: 14 }}>
            <div>
              <h1 style={{ fontSize: 30, fontWeight: 700, letterSpacing: '-0.02em', color: C.navy, margin: 0 }}>My projects</h1>
              <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>{drafts.length} {drafts.length === 1 ? 'project' : 'projects'} saved</div>
            </div>
            <button data-testid="new-project-btn" onClick={newProject}
              style={{ background: C.navy, color: '#fff', border: 'none', padding: '12px 22px', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer', boxShadow: '0 2px 6px rgba(15,39,66,0.18)' }}>
              ＋ Start a new project
            </button>
          </div>

          {drafts.length === 0 ? (
            <div data-testid="empty-state" style={{ padding: 48, background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, textAlign: 'center' }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>📐</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: C.navy }}>No projects yet</div>
              <div style={{ fontSize: 13, color: C.muted, marginTop: 6 }}>Start a new SCOPE project for a full Schedule of Works, or get a 30-second ballpark estimate first.</div>
              <div style={{ marginTop: 18, display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                <button onClick={newProject} data-testid="empty-start-scope" style={{ background: C.navy, color: '#fff', border: 'none', padding: '12px 22px', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>Start in SCOPE →</button>
                <a href="/estimate" data-testid="empty-start-ballpark" style={{ background: '#fff', color: C.navy, border: `1.5px solid ${C.navy}`, padding: '12px 22px', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer', textDecoration: 'none' }}>💷 Ballpark estimate →</a>
              </div>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
                <h2 style={{ fontSize: 20, fontWeight: 700, color: C.navy, margin: 0 }}>Your projects</h2>
                <div style={{ display: 'flex', gap: 8 }}>
                  <a href="/estimate" data-testid="cta-ballpark-quick" style={{ background: '#fff', color: C.navy, border: `1.5px solid ${C.border}`, padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 700, textDecoration: 'none' }}>💷 Get a ballpark</a>
                  <button onClick={newProject} data-testid="cta-new-scope" style={{ background: C.navy, color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>＋ New SCOPE project</button>
                </div>
              </div>
              <div id="projects" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 16 }}>
                {drafts.map(d => (
                  <ProjectCard key={d.draft_id} d={d}
                    onUnlock={handleUnlock}
                    onContinue={handleContinue}
                    onView={handleViewDownloads}
                    onDelete={handleDelete}
                    isOpen={openDraftId === d.draft_id} />
                ))}
              </div>
            </>
          )}

          {openDraft && openDraft.documents_paid && (
            <div id="documents" style={{ marginTop: 36 }}>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: C.navy, margin: '0 0 12px' }}>Downloads — {openDraft.address || openDraft.project_name}</h2>
              <DocumentTiles draft={openDraft} />
            </div>
          )}

          {/* Soft inline QS-call helper — tiny phone icon + tooltip-style line */}
          {drafts.length > 0 && (
            <SoftQSCallBar drafts={drafts} userEmail={me.email} />
          )}

          {/* Optional add-ons catalogue (Damage Repairs + Whole-House Composites) */}
          <OptionalAddonsPanel />
        </main>
      </div>
      <Footer />
      {/* Bottom-right floating QS Review Call CTA — pinned across scroll */}
      <QSFloatingCTA
        userEmail={me?.email || ''}
        draftId={openDraft?.draft_id || drafts[0]?.draft_id || null}
      />
    </div>
  );
}

// Soft, low-friction QS call helper — sits at the bottom of the dashboard.
// Click to expand → pick the project → hit Book.
function SoftQSCallBar({ drafts, userEmail }) {
  const [expanded, setExpanded] = useState(false);
  const [pickedDraftId, setPickedDraftId] = useState(drafts[0]?.draft_id || '');
  return (
    <div id="qs-call" data-testid="qs-call-section" style={{ marginTop: 32, marginBottom: 8 }}>
      {!expanded ? (
        <button
          data-testid="qs-call-soft-trigger"
          onClick={() => setExpanded(true)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '7px 14px', borderRadius: 999,
            background: '#fff', border: `1px solid ${C.border}`,
            color: C.text2, fontSize: 12.5, fontWeight: 500,
            cursor: 'pointer', transition: '0.15s',
            boxShadow: '0 1px 3px rgba(15,39,66,0.04)',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = C.blue; e.currentTarget.style.color = C.blueDark; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.text2; }}
        >
          <span style={{ fontSize: 13 }}>📞</span>
          Need to speak with a QS?
        </button>
      ) : (
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 10,
          padding: '8px 12px 8px 14px', borderRadius: 999,
          background: '#fff', border: `1px solid ${C.blue}`,
          flexWrap: 'wrap', maxWidth: '100%',
          boxShadow: '0 2px 8px rgba(37,99,235,0.12)',
        }}>
          <span style={{ fontSize: 14 }}>📞</span>
          <span style={{ fontSize: 12.5, color: C.text2 }}>QS call about</span>
          <select
            data-testid="qs-draft-picker"
            value={pickedDraftId}
            onChange={(e) => setPickedDraftId(e.target.value)}
            style={{
              fontSize: 12.5, padding: '4px 8px', borderRadius: 6,
              border: `1px solid ${C.border2}`, background: '#fff', color: C.navy,
              maxWidth: 240, cursor: 'pointer',
            }}
          >
            {drafts.map(d => (
              <option key={d.draft_id} value={d.draft_id}>
                {(d.project_name || d.address || 'Untitled')} — {d.draft_id.slice(0, 8)}
              </option>
            ))}
          </select>
          <QSCallCTA variant="compact" userEmail={userEmail} draftId={pickedDraftId} testid="qs-call-cta-dashboard" />
          <button
            onClick={() => setExpanded(false)}
            data-testid="qs-call-collapse"
            style={{ background: 'transparent', border: 'none', color: C.muted, fontSize: 16, cursor: 'pointer', padding: 4 }}
            aria-label="Close"
          >×</button>
        </div>
      )}
    </div>
  );
}
